import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { loginUser, verifyMfa } from '../../../_actions/user_actions';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { Form, Input, Button, Checkbox, Typography } from 'antd';
import {
  UserOutlined,
  LockOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';

import { useDispatch } from 'react-redux';

const { Title } = Typography;

function LoginPage(props) {
  const dispatch = useDispatch();
  const rememberMeChecked = localStorage.getItem('rememberMe') ? true : false;

  // ログイン後の遷移先を returnTo から決定（オープンリダイレクト防止のガード付き）
  const getRedirect = () => {
    try {
      const p = new URLSearchParams(props.location.search).get('returnTo');
      if (p && p.startsWith('/') && !p.startsWith('//') && !p.startsWith('/login')) {
        return p;
      }
    } catch (e) {}
    return '/';
  };

  // トライアル簡易登録から「既にアカウントが存在する」場合に付与される ?email= を取得
  const getQueryEmail = () => {
    try {
      return new URLSearchParams(props.location.search).get('email') || '';
    } catch (e) {}
    return '';
  };
  const queryEmail = getQueryEmail();

  const [formErrorMessage, setFormErrorMessage] = useState('');
  const [rememberMe, setRememberMe] = useState(rememberMeChecked);

  // MFA関連のstate
  const [mfaRequired, setMfaRequired] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); // 5分 = 300秒
  const [timerActive, setTimerActive] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');

  // 5分タイマー
  useEffect(() => {
    let interval = null;
    if (timerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft((time) => time - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      setMfaError(
        '認証コードの有効期限が切れました。再度ログインしてください。'
      );
      setTimeout(() => {
        // 初期状態に戻す
        setMfaRequired(false);
        setTempToken('');
        setMfaCode('');
        setMfaError('');
        setTimeLeft(300);
        setTimerActive(false);
        setLoginEmail('');
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [timerActive, timeLeft]);

  const handleRememberMe = () => {
    setRememberMe(!rememberMe);
  };

  // MFAコード入力変更ハンドラー
  const handleMfaCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setMfaCode(value);
    setMfaError('');
  };

  // MFA認証処理
  const handleMfaVerify = () => {
    if (mfaCode.length !== 6) {
      setMfaError('6桁の認証コードを入力してください。');
      return;
    }

    dispatch(verifyMfa({ tempToken, code: mfaCode }))
      .then((response) => {
        if (response.payload.loginSuccess) {
          window.localStorage.setItem('w_authExp', response.payload.w_authExp);
          window.localStorage.setItem('w_auth', response.payload.w_auth);
          if (rememberMe === true) {
            window.localStorage.setItem('rememberMe', loginEmail);
          }
          // returnTo があれば元の位置へ復帰、無ければトップへ
          const dest = getRedirect();
          if (dest === '/') props.history.push('/');
          else props.history.replace(dest);
        } else {
          if (response.payload.expired) {
            setMfaError(
              '認証コードの有効期限が切れました。再度ログインしてください。'
            );
            setTimeout(() => {
              setMfaRequired(false);
              setTempToken('');
              setMfaCode('');
              setMfaError('');
              setTimeLeft(300);
              setTimerActive(false);
              setLoginEmail('');
            }, 3000);
          } else {
            setMfaError('認証コードが正しくありません。');
          }
        }
      })
      .catch(() => {
        setMfaError('認証処理中にエラーが発生しました。');
      });
  };

  // MFAキャンセル
  const handleMfaCancel = () => {
    setMfaRequired(false);
    setTempToken('');
    setMfaCode('');
    setMfaError('');
    setTimeLeft(300);
    setTimerActive(false);
    setLoginEmail('');
  };

  // 初期メールアドレスは「?email= クエリ」を優先し、無ければ記憶したアカウントを使う
  const initialEmail = queryEmail
    ? queryEmail
    : localStorage.getItem('rememberMe')
    ? localStorage.getItem('rememberMe')
    : '';

  return (
    <Formik
      initialValues={{
        email: initialEmail,
        password: '',
      }}
      validationSchema={Yup.object().shape({
        email: Yup.string()
          .email('メールの形式ではありません。')
          .required('メールを入力してください。'),
        password: Yup.string()
          .min(2, 'パスワードは2文字以上です。')
          .required('パスワードを入力してください。'),
      })}
      onSubmit={(values, { setSubmitting }) => {
        setTimeout(() => {
          let dataToSubmit = {
            email: values.email,
            password: values.password,
          };

          dispatch(loginUser(dataToSubmit))
            .then((response) => {
              // console.log({ response });
              // MFAが必要な場合
              if (response.payload.mfaRequired) {
                setMfaRequired(true);
                setTempToken(response.payload.tempToken);
                setLoginEmail(values.email);
                setTimeLeft(300);
                setTimerActive(true);
                setFormErrorMessage('');
              } else if (response.payload.loginSuccess) {
                // 通常ログイン成功
                window.localStorage.setItem(
                  'w_authExp',
                  response.payload.w_authExp
                );
                window.localStorage.setItem('w_auth', response.payload.w_auth);
                if (rememberMe === true) {
                  window.localStorage.setItem('rememberMe', values.email);
                } else {
                  localStorage.removeItem('rememberMe');
                }
                // returnTo があれば元の位置へ復帰、無ければトップへ
                const dest = getRedirect();
                if (dest === '/') props.history.push('/');
                else props.history.replace(dest);
              } else {
                setFormErrorMessage(
                  'メールとパスワードをもう一度確認してください。'
                );
              }
            })
            .catch((err) => {
              setFormErrorMessage(
                'メールとパスワードをもう一度確認してください。'
              );
              setTimeout(() => {
                setFormErrorMessage('');
              }, 3000);
            });
          setSubmitting(false);
        }, 500);
      }}
    >
      {(props) => {
        const {
          values,
          touched,
          errors,
          dirty,
          isSubmitting,
          handleChange,
          handleBlur,
          handleSubmit,
          handleReset,
        } = props;
        return (
          <div className="app">
            <Title level={2}>ログイン</Title>
            {/* トライアル簡易登録から既存アカウントとして誘導された場合の案内 */}
            {queryEmail && (
              <p
                style={{
                  width: '350px',
                  marginBottom: 'var(--space-4)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--grey-65)',
                  lineHeight: 'var(--leading-normal)',
                }}
              >
                このメールアドレスは既に登録されています。パスワードでログインしてください。
              </p>
            )}
            <form onSubmit={handleSubmit} style={{ width: '350px' }}>
              <Form.Item required>
                <Input
                  id="email"
                  prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                  placeholder="メールを入力してください。"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={mfaRequired}
                  aria-invalid={errors.email && touched.email ? 'true' : undefined}
                  aria-describedby={
                    errors.email && touched.email ? 'email-error' : undefined
                  }
                  className={
                    errors.email && touched.email
                      ? 'text-input error'
                      : 'text-input'
                  }
                />
                {errors.email && touched.email && (
                  <div id="email-error" role="alert" className="input-feedback">
                    <br />
                    <CloseCircleOutlined />
                    {errors.email}
                  </div>
                )}
              </Form.Item>
              <br />
              <Form.Item required>
                <Input
                  id="password"
                  prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                  placeholder="パスワードを入力してください。"
                  type="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  disabled={mfaRequired}
                  aria-invalid={
                    errors.password && touched.password ? 'true' : undefined
                  }
                  aria-describedby={
                    errors.password && touched.password
                      ? 'password-error'
                      : undefined
                  }
                  className={
                    errors.password && touched.password
                      ? 'text-input error'
                      : 'text-input'
                  }
                />
                {errors.password && touched.password && (
                  <div
                    id="password-error"
                    role="alert"
                    className="input-feedback"
                  >
                    <br />
                    <CloseCircleOutlined />
                    {errors.password}
                  </div>
                )}
              </Form.Item>

              {/* MFA認証コード入力欄 */}
              {mfaRequired && (
                <>
                  <br />
                  <div
                    style={{
                      padding: '1rem',
                      backgroundColor: '#f0f2f5',
                      borderRadius: '8px',
                      marginBottom: '1rem',
                    }}
                  >
                    <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
                      ■ 認証コード入力
                    </p>
                    <p style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                      メールアドレスに6桁の認証コードを送信しました。
                    </p>
                    <p style={{ fontSize: '0.9rem', color: 'var(--blue-active)' }}>
                      残り時間: {Math.floor(timeLeft / 60)}分{timeLeft % 60}秒
                    </p>
                  </div>
                  <Form.Item>
                    <Input
                      placeholder="認証コード（6桁）"
                      value={mfaCode}
                      onChange={handleMfaCodeChange}
                      maxLength={6}
                      style={{ fontSize: '1.2rem', textAlign: 'center' }}
                      disabled={timeLeft === 0}
                    />
                  </Form.Item>
                  {mfaError && (
                    <label>
                      <p
                        role="alert"
                        style={{
                          color: '#ff0000bf',
                          fontSize: '0.7rem',
                          border: '1px solid',
                          padding: '1rem',
                          borderRadius: '10px',
                          marginBottom: '1rem',
                        }}
                      >
                        {mfaError}
                      </p>
                    </label>
                  )}
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <Button
                      type="primary"
                      onClick={handleMfaVerify}
                      style={{ flex: 1 }}
                      disabled={timeLeft === 0 || mfaCode.length !== 6}
                    >
                      認証する
                    </Button>
                    <Button onClick={handleMfaCancel} style={{ flex: 1 }}>
                      キャンセル
                    </Button>
                  </div>
                </>
              )}

              <br></br>
              {formErrorMessage && !mfaRequired && (
                <label>
                  <p
                    role="alert"
                    style={{
                      color: '#ff0000bf',
                      fontSize: '0.7rem',
                      border: '1px solid',
                      padding: '1rem',
                      borderRadius: '10px',
                    }}
                  >
                    {formErrorMessage}
                  </p>
                </label>
              )}

              {!mfaRequired && (
                <Form.Item>
                  <Checkbox
                    id="rememberMe"
                    onChange={handleRememberMe}
                    checked={rememberMe}
                    disabled={mfaRequired}
                  >
                    このアカウントを記憶
                  </Checkbox>
                  <a
                    className="login-form-forgot"
                    href="/resetuser"
                    style={{
                      float: 'right',
                      pointerEvents: mfaRequired ? 'none' : 'auto',
                      color: mfaRequired ? '#ccc' : '',
                    }}
                  >
                    パスワードを忘れた方
                  </a>
                  <div>
                    {/* ログインボタンはアクセントカラー水色（2 次トラック S1 / item 19）。
                        antd に accent type が無いため type="default" + className=".ds-btn-accent"。
                        ※ MFA「認証する」ボタンは認証フロー（§5 ガード）のため本変更の対象外。 */}
                    <Button
                      type="default"
                      htmlType="submit"
                      className="login-form-button ds-btn-accent"
                      style={{ minWidth: '100%' }}
                      disabled={isSubmitting || mfaRequired}
                      onSubmit={handleSubmit}
                    >
                      ログイン
                    </Button>
                  </div>
                </Form.Item>
              )}
            </form>
          </div>
        );
      }}
    </Formik>
  );
}

export default withRouter(LoginPage);
